import React, { useState, useEffect } from "react";
import Nav from 'react-bootstrap/Nav';
import { TabularPreviewTable } from '../../../components/tables/TabularPreviewTable';
import { fetchPreviewData } from '../../../services/api';
import { sandboxArtifactId } from '../../../utils/flowOutputRef';
import { previewTotalRows, rowsFromParseOutput } from '../../../utils/tabularPreview';
import { NodeEmptyState } from '../../../components/nodes/NodeEmptyState';
import { isTabularPayload, resolveNodeEmptyReason } from '../../../utils/nodeEmptyState';
import { ResolutionType } from '../../../constants';
import nodeSettings from '../../../contracts/nodeSettings.json';

/** The words of each mode and of each select, which an agent's review card
 *  and the backend say them in too (contracts/nodeSettings.json). */
const MODE_LABELS: Record<string, string> = nodeSettings.dataPool.modes;
const SELECT_LABELS = nodeSettings.dataPool.settings;

/** One conflict mode: a label and a select over every ResolutionType. */
function ModeSelect({ label, value, onChange, testId }: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  testId: string;
}) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 4, margin: 0, whiteSpace: 'nowrap' }}>
      <span>{label}</span>
      <select
        aria-label={label}
        data-testid={testId}
        value={value}
        onChange={(event) => onChange?.(event.target.value)}
        style={{ padding: '1px 4px', fontSize: 12 }}
      >
        {Object.values(ResolutionType).map((mode) => (
          <option key={mode} value={mode}>{MODE_LABELS[mode]}</option>
        ))}
      </select>
    </label>
  );
}

interface DataPoolContentProps {
  activeTab: string;
  onSelectTab: (tab: string) => void;
  tabData: any[];
  tableData: Record<string, unknown>[];
  data?: any;
  /**
   * Is anything wired into this pool's input?
   *
   * Only the behavior can see the graph, so it is passed down. Defaults to
   * FALSE, which is the honest reading when a caller has not said: an
   * unconnected pool is the commonest way to arrive at an empty body, and
   * claiming otherwise would send the user to run a node that does not exist.
   */
  connected?: boolean;
  /**
   * Did a node feeding this one run and fail?
   *
   * Same reasoning as ``connected``: only the behavior can see the graph and
   * the exec status. Defaults to FALSE so a caller that has not said is treated
   * as "no known failure" rather than blaming an upstream node at random.
   */
  upstreamErrored?: boolean;
  /** On a dashboard, is a viewer's run making the pool's rows? */
  upstreamRunning?: boolean;
  /** On a dashboard, why the viewer's run stopped before the pool, if it did. */
  upstreamStopped?: string | null;
  /** Are the rows of the input that just arrived still being read? */
  reading?: boolean;
  /** A dashboard's tile: an input not in yet is on its way, with no node to run. */
  onDashboard?: boolean;
  /** How the selects of one chart combine (a ResolutionType). */
  insideChartMode?: string;
  /** How the latest selections of the linked charts combine (a ResolutionType). */
  betweenChartsMode?: string;
  onInsideChartModeChange?: (mode: string) => void;
  onBetweenChartsModeChange?: (mode: string) => void;
}

const ContentComponent = ({
  outputTable,
  data,
}: {
  outputTable: any;
  data: any;
}) => {
  const [previewTable, setPreviewTable] = useState<any[]>([]);
  // The server previews the first 100 rows and says how many there are.
  const [previewTotal, setPreviewTotal] = useState<number | undefined>(undefined);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [usePreview, setUsePreview] = useState(false);

  // When outputTable changes (e.g. from an interaction), override the stale
  // server preview so the live interacted data is always shown.
  useEffect(() => {
    if (!outputTable || outputTable.length === 0) return;
    setUsePreview(false);
    setIsLoadingPreview(false);
  }, [outputTable]);

  useEffect(() => {
      let cancelled = false;

      const loadPreviewData = async () => {
          const fileId = sandboxArtifactId(data.input);

          if (!fileId) {
              setPreviewTable([]);
              setUsePreview(false);
              return;
          }

          setIsLoadingPreview(true);
          try {
              const previewData = await fetchPreviewData(fileId);
              const nextPreviewTable = rowsFromParseOutput(previewData);

              if (cancelled) return;

              setPreviewTable(nextPreviewTable);
              setPreviewTotal(previewTotalRows(previewData));
              // Keep the already-rendered output table when preview returns
              // no rows or resolves after the input has moved on.
              setUsePreview(nextPreviewTable.length > 0);
          } catch (error) {
              if (cancelled) return;
              console.log("[ContentComponent] Preview fetch failed, falling back to outputTable:", error);
              setUsePreview(false);
          } finally {
              if (!cancelled) {
                  setIsLoadingPreview(false);
              }
          }
      };

      loadPreviewData();

      return () => {
          cancelled = true;
      };
  }, [data.input]);

  // Use preview data if available, otherwise fall back to outputTable. Either
  // way the table shows at most 100 rows and says how many there are.
  const showingPreview = usePreview && previewTable.length > 0;
  const displayTable = showingPreview ? previewTable : outputTable;
  const displayTotal = showingPreview ? previewTotal : outputTable?.length;

  return (
      <div
          className="nowheel"
          data-curio-datapool-scroll="true"
          // overflow:'auto' is what makes this the scroll owner. The outer clamp
          // below is overflow:'hidden', so without it the rows are simply clipped
          // and the user has to resize the node to see them (#156).
          style={{ flex: 1, minHeight: 0, minWidth: 0, display: "flex", flexDirection: "column", overflow: "auto" }}
      >
          <TabularPreviewTable
              rows={displayTable}
              totalRows={displayTotal}
              rowKeyPrefix={data.nodeId}
              loading={isLoadingPreview}
              excludeColumns={[]}
          />
      </div>
  );
};

export default function DataPoolContent({
  activeTab, onSelectTab, tabData, tableData, data = { nodeId: '', input: '' }, connected = false, upstreamErrored = false,
  upstreamRunning = false, upstreamStopped = null, reading = false, onDashboard = false,
  insideChartMode = ResolutionType.OVERWRITE, betweenChartsMode = ResolutionType.OVERWRITE,
  onInsideChartModeChange, onBetweenChartsModeChange,
}: DataPoolContentProps) {
  const wrappers: any[] = (() => {
    if (!data.input || typeof data.input !== "object") return [];
    if (data.input.dataType === "outputs" && Array.isArray(data.input.data)) return data.input.data;
    return [data.input];
  })();

  // Tabs derived from expanding a single artifact (autk-grammar's multi-layer
  // wrapper persists as one `dict`-kind ref whose payload is an `outputs`
  // envelope — see useTableData.processDataAsync) have no 1:1 wrapper ref:
  // wrappers.length === 1 but tabData.length === N. Don't pipe wrappers[0]
  // into tab 0 in that case — its preview fetch round-trips the whole
  // multi-MB outputs payload only to fall back to outputTable (the artifact's
  // dataType is `dict`, which has no previewable shape), so tab 0 lags while
  // every other tab is instant. Force-empty input on all tabs makes them
  // uniformly use outputTable, which already holds the active layer's data.
  const expandedFromSingleRef = wrappers.length === 1 && tabData.length > 1;

  // Render only the active tab's content directly instead of via Tab.Pane —
  // react-bootstrap's .tab-pane has display:none/block CSS that fights any
  // attempt to make it a flex child, which broke height propagation to the
  // inner scroll div. Manual rendering keeps a clean flex chain:
  // outer flex column → flex:1 content area → ContentComponent's overflow:auto.
  const hasData = Array.isArray(tabData) && tabData.length > 0;
  const activeIndex = parseInt(activeTab) || 0;
  const activeTabInput = hasData && !expandedFromSingleRef ? (wrappers[activeIndex] ?? '') : '';

  return (
    // overflow:hidden + height:100% pin this component to the parent's box, so
    // only the inner scroll engages — without this clamp, the outer NodeEditor
    // Tab.Pane (overflowY:auto) would scroll the whole node including the tab strip.
    // paddingBottom keeps the table's horizontal scrollbar clear of the play
    // button and NodeEditor's bottom tab nav (both occupy ~25px below NodeEditor's
    // outer div via marginTop:-25px / overflow:visible).
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, minWidth: 0, paddingBottom: 25 }}>
      {/* How the selections that reach the pool combine (#581). nodrag/nopan
          so React Flow leaves the selects to the pointer. */}
      <div
        className="nodrag nopan nowheel"
        data-testid="data-pool-modes"
        style={{ flexShrink: 0, display: 'flex', flexWrap: 'wrap', columnGap: 12, rowGap: 4, marginBottom: 6, fontSize: 12 }}
      >
        <ModeSelect
          label={SELECT_LABELS.insideChart}
          value={insideChartMode}
          onChange={onInsideChartModeChange}
          testId="data-pool-mode-inside"
        />
        <ModeSelect
          label={SELECT_LABELS.betweenCharts}
          value={betweenChartsMode}
          onChange={onBetweenChartsModeChange}
          testId="data-pool-mode-between"
        />
      </div>
      <Nav
        variant="tabs"
        activeKey={activeTab}
        onSelect={(k) => onSelectTab(k || '0')}
        className="mb-3 nowheel"
        // nowrap + overflowX keeps the strip one row tall regardless of table
        // count, so adding tables never steals height from the table below.
        // overflowY hidden: a tab's -1px bottom margin would otherwise make
        // the strip scroll vertically by one pixel, with a scrollbar.
        // `nowheel` stops React Flow swallowing the scroll (#156).
        style={{ flexShrink: 0, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden' }}
        data-testid="data-pool-tabs"
      >
        {hasData ? (
          tabData.map((entry, index) => {
            const title = entry && typeof entry === 'object' && entry.layerName
              ? String(entry.layerName)
              : `Tab ${index + 1}`;
            return (
              <Nav.Item key={index}>
                <Nav.Link eventKey={index.toString()}>{title}</Nav.Link>
              </Nav.Item>
            );
          })
        ) : null}
      </Nav>
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {hasData ? (
          <ContentComponent
            key={activeIndex}
            outputTable={tableData}
            data={{ ...data, input: activeTabInput }}
          />
        ) : (
          // "No data available." was true but not useful: it named the state
          // and not the cause, so an unconnected pool and one whose upstream had
          // not run said the same thing (#224). The fake "No Data" TAB above it
          // is gone for the same reason — it looked like a tab you could open.
          (() => {
            const reason = resolveNodeEmptyReason({
              connected,
              upstreamErrored,
              upstreamRunning,
              upstreamStopped,
              hasInput: data?.input != null && data.input !== '',
              reading,
              onDashboard,
              // Asked of the PAYLOAD, not of tabData (#347). The premise this
              // used to state - that a zero-row dataframe still yields a tab
              // and so never reaches here - is not true: processDataAsync
              // filters dataframe/geodataframe layers with no rows out before
              // tabData is set (autk-db cannot load an empty layer). So a
              // node that ran and returned an empty table landed here and was
              // told "This input is not tabular data", which is wrong and
              // unactionable. The declared dataType still knows better.
              tabular: isTabularPayload(data?.input),
              rowCount: 0,
            }) ?? 'no-rows';
            return (
              <NodeEmptyState
                reason={reason}
                hint={reason === 'upstream-stopped' ? upstreamStopped ?? undefined : undefined}
              />
            );
          })()
        )}
      </div>
    </div>
  );
}
